import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { pluralLabel } from 'LearningManager/helpers/role';
import { CourseAssignmentOption } from 'LearningManager/pages/CourseAssignments';
import useLearnerCount from 'LearningManager/hooks/useLearnerCount';
import useTenantScope from 'LearningManager/hooks/useTenantScope';
import styles from './CourseUnassignModal.module.scss';

interface CourseUnassignModalProps {
	visible: boolean;
	courseId: number;
	options: CourseAssignmentOption[];
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const CourseUnassignModal: React.FC<CourseUnassignModalProps> = ({
	visible,
	courseId,
	options,
	onCancel,
	onConfirm
}) => {
	const [userCount, setUserCount] = useState(0);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const { globalTenantId } = useTenantScope();
	const { loading, getCount } = useLearnerCount(globalTenantId);
	const { t } = useTranslation();
	const hasSelection = selectedIds.length > 0;

	const selectAssignment = async (ids: number[]) => {
		setSelectedIds(ids);
		if(ids.length && courseId) {
			const { count } = await getCount('course', { assignment_source_ids: ids }, false, courseId);
			setUserCount(count);
		} else setUserCount(0);
	}

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title={t('learning.courseAssignments.courseUnassignModal.title')}
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						{t('learning.courseAssignments.courseUnassignModal.cancel')}
					</LMSButton>

					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						{t('learning.courseAssignments.courseUnassignModal.unassign')}
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					{t(
						'learning.courseAssignments.courseUnassignModal.selectAssignments'
					)}
				</p>

				<div className={styles.field}>
					<div className={styles.field__label}>
						{t(
							'learning.courseAssignments.courseUnassignModal.assignment'
						)}
					</div>

					<MultiSelectDropdown
						options={options.map(({id, name}) => ({ id, label: name }))}
						placeholder={t(
							'learning.courseAssignments.courseUnassignModal.selectAssignment'
						)}
						defaultSelectedIds={[]}
						onChange={selectAssignment}
						allLabel={t(
							'learning.courseAssignments.courseUnassignModal.allAssignments'
						)}
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{hasSelection && (
					<div className={styles.summary}>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseUnassignModal.description'
							)}
						</p>
							<ul className={styles.list}>
								<li>
									{t(
										'learning.courseAssignments.courseUnassignModal.affect'
									)}{' '}
									{loading ? (
										<Icon
											type="loading"
											aria-label={t(
												'learning.courseAssignments.courseUnassignModal.countingUsers'
											)}
										/>
									) : (
										t(
											pluralLabel('learning.courseAssignments.courseUnassignModal.learners', userCount),
											{
												count: userCount
											}
										)
									)}{' '}
									{t(
										'learning.courseAssignments.courseUnassignModal.total'
									)}
								</li>
							</ul>
						
						<div className={styles.warning}>
							<Icon type="warningFilledIcon" role="img" />
							{t(
								'learning.courseAssignments.courseUnassignModal.warning'
							)}
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseUnassignModal;